<template lang="pug">
main.app-main

    //- Is loading
    section.wam-content.full-width(v-if="!criteriaListIsReady")
        spinner.l-flex-row--center.l-align-center(width="200" height="200")

    template(v-else)
        //- Zero State
        template(v-if="audienceListIsEmpty")
            section.wam-content.full-width
                section.l-flex-column.is-empty.zero-state
                    p.zero-state-message Oh no 😕
                        |
                        br
                        | There is no audience available yet.
                        br
                        |
                        router-link(
                        @click="changeViewName('Builder')"
                        :to="'/' + accountId + '/builder/segment'") Create one in the Audience Builder
        //- Data Monitoring
        template(v-else)

            section.wam-content.monitoring(:class="{'is-selected': selectedAudienceId !== null}")
                //- MODAL - Delete Audience
                div.overlay-close(
                        v-if='displayAudienceConfirmationModal || displaySharingConfirmationModal',
                        @click='closeModal()',
                    )
                div.modal.modal__small.modal-delete(
                        v-if='displayAudienceConfirmationModal'
                        :class="{'loading': loadingDeleteAudience}"
                    )
                    ConfirmationModal
                        template(slot='content')
                            template(v-if="!loadingDeleteAudience")
                                div You are about to delete audience
                                    |
                                    span.is-highlighted__danger &nbsp{{ deletedAudienceId }}
                                div Do you want to continue?
                            template(v-else)
                                spinner.l-flex-row--center.l-align-center(
                                    width="200"
                                    height="200"
                                )
                        template(slot='footer', v-if="!loadingDeleteAudience")
                            div.footer-content.l-flex-row--between
                                div.btn.btn--medium(@click='setDisplayModal({ item: "audience", display: false })') Cancel
                                div.btn.btn--medium.btn-rev.btn-delete(@click.once='confirmDeleteAudience(deletedAudienceId)') Delete
                //- MODAL - stop sharing
                div.modal.modal__small.modal-delete(v-if='displaySharingConfirmationModal')
                    ConfirmationModal
                        template(slot='content')
                            div Are you sure you want to stop sharing with
                                |
                                |
                                span.is-highlighted__danger {{deletedSharedAccount.id}} - {{deletedSharedAccount.label}}
                                | ?
                        template(slot='footer')
                            div.footer-content.l-flex-row--between
                                div.btn.btn--medium(@click='cancelStopSharing(deletedSharedAccount.id)') Cancel
                                div.btn.btn--medium.btn-rev.btn-delete(@click.once='stopSharing(deletedSharedAccount.id)') Stop

                //- MODAL - Delete transfer
                div.overlay-close(v-if='displayTransferConfirmationModal', @click='setDisplayModal({ item: "transfer", display: false })')
                div.modal.modal__small.modal-delete(v-if='displayTransferConfirmationModal')
                    ConfirmationModal
                        template(slot='content')
                            div You are about to delete transfer
                                |
                                |
                                span.is-highlighted__danger {{ deletedTransfer.transferId }}
                            div Do you want to continue?
                        template(slot='footer')
                            div.footer-content.l-flex-row--between
                                div.btn.btn--medium(@click='setDisplayModal({ item: "transfer", display: false })') Cancel
                                div.btn.btn--medium.btn-rev.btn-delete(@click.once='deleteTransfer(deletedTransfer)') Delete

                //- MODAL - Delete volume alert
                div.overlay-close(v-if='displayVolumeAlertConfirmationModal', @click='setDisplayModal({ item: "volumeAlert", display: false })')
                div.modal.modal__small.modal-delete-audience(v-if='displayVolumeAlertConfirmationModal')
                    ConfirmationModal
                        template(slot='content')
                            div You are about to delete volume alert
                                |
                                |
                                span.is-highlighted__danger {{ deletedVolumeAlertId }}
                            div Do you want to continue?
                        template(slot='footer')
                            div.form-footer-content.l-flex-row--between
                                div.btn.btn--medium(@click='setDisplayModal({ item: "volumeAlert", display: false })') Cancel
                                div.btn.btn--medium.btn-rev.btn-delete(@click.once='deleteVolumeAlert({audienceId: selectedAudienceId, volumeAlertId: deletedVolumeAlertId})') Delete

                //- Monitoring
                Audiences(@trigger-bootstrap="bootstrapAudience")
                //- Details
                Details(:audienceInfo="audienceInfo", ref="details")
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'

//components
import Audiences from '@/components/monitoring/Audiences'
import Details from '@/components/monitoring/Details'
import ConfirmationModal from '@/components/common/confirmationModal'

import { get, isEmpty, isNil } from 'lodash'

export default {
    name: 'Monitoring',

    components: {
        Audiences,
        Details,
        ConfirmationModal
    },

    data() {
        return {
            displayMainMenu: false,
            loadingDeleteAudience: false
        }
    },

    watch: {
        selectedAudienceId: {
            immediate: true,
            handler(value) {
                if (!isNil(value)) {
                    this.retrieveVolumeAlert({ audienceId: this.selectedAudienceId })
                }
            }
        }
    },

    computed: {
        // Call to state
        ...mapState('data/audience', {
            audienceList: state => state.list
        }),
        ...mapState('account', {
            accountId: state => state.accountId,
            userRole: state => state.userRole,
            attributionCounterAudience: state => state.attributionCounterAudience,
            dailyBootstrapLimit: state => state.dailyBootstrapLimit
        }),
        ...mapState('ui/monitoring', {
            selectedAudienceId: state => state.selectedRowId,
            selectedAudienceHaveDeletedCriteria: state => state.selectedAudienceHaveDeletedCriteria,
            displayAudienceConfirmationModal: state => state.displayModal.audience,
            displayTransferConfirmationModal: state => state.displayModal.transfer,
            displaySharingConfirmationModal: state => state.displayModal.sharing,
            displayVolumeAlertConfirmationModal: state => state.displayModal.volumeAlert,
            deletedAudienceId: state => state.deletedAudienceId,
            deletedTransfer: state => state.deletedTransfer,
            deletedVolumeAlertId: state => state.deletedVolumeAlertId,
            deletedSharedAccount: state => state.deletedSharedAccount,
            genericFormulaVolumes: state => state.formulaVolumes
        }),
        ...mapState('ui/settings', {
            bootstrapPush: state => state.general.bootstrapPush
        }),
        ...mapState('ui/builder', {
            formulaLoading: state => state.isLoading.volumes
        }),

        // Call to getters
        ...mapGetters('data/audience', ['getItemById']),
        ...mapGetters('ui/builder', ['criteriaListIsReady', 'getThirdPartyOnlyInAudienceFormula']),
        ...mapGetters('account', ['getUserAccess', 'getIsMonitoringActionAllowed']),

        audienceListIsEmpty() {
            return isEmpty(this.audienceList)
        },

        audienceInfo() {
            const _info = this.getItemById({ id: this.selectedAudienceId })
            // @TODO error notificationselectedAudienceHaveDeletedCriteria
            return _info == false ? null : _info
        },

        monitoringActionAllowed() {
            return this.getIsMonitoringActionAllowed()
        },

        displayBootstrapAction() {
            if (this.audienceInfo) {
                const canShowBootstrap = this.audienceInfo.shouldShowBootstrap({
                    bootstrapPush: this.bootstrapPush,
                    managerAccess: this.getUserAccess({ type: 'manager_access' }),
                    userRole: this.userRole
                })

                const isNotThirdPartyBootstrap = !this.audienceInfo.isThirdPartyBootstrap({
                    bootstrapPush: this.bootstrapPush,
                    managerAccess: this.getUserAccess({ type: 'manager_access' }),
                    userRole: this.userRole
                })

                const asNoBootstrap = this.audienceInfo.bootstrap === 'none'

                const isBootstrapAllowed = this.audienceInfo.isBootstrapAllowed({
                    userRole: this.userRole,
                    getThirdPartyOnlyInAudienceFormula: this.getThirdPartyOnlyInAudienceFormula
                })

                const dailyQuotaNotBeenReached =
                    this.userRole === 'webo_admin' ||
                    this.attributionCounterAudience + get(this.genericFormulaVolumes, `${this.selectedAudienceId}`, 0) < this.dailyBootstrapLimit

                return (
                    canShowBootstrap &&
                    isNotThirdPartyBootstrap &&
                    asNoBootstrap &&
                    isBootstrapAllowed &&
                    dailyQuotaNotBeenReached &&
                    !this.formulaLoading &&
                    !this.selectedAudienceHaveDeletedCriteria
                )
            }
            return false
        },

        // Volume Alert
        volumeAlertLoading() {
            return this.audienceInfo.volumeAlert.isLoading
        },
        volumeAlert() {
            if (isEmpty(this.audienceInfo.volumeAlert.details)) {
                return {
                    label: 'Create volume alert ',
                    action: this.volumeAlertLoading ? null : this.addVolumeAlert
                }
            }
            return {
                label: 'Edit volume alert',
                action: this.volumeAlertLoading ? null : this.displayEditFormVolumeAlert
            }
        }
    },
    methods: {
        // Call to actions
        ...mapActions('data/audience', ['retrieveItems', 'deleteAudience', 'deleteVolumeAlert', 'retrieveVolumeAlert']),
        ...mapActions('data/transfer', ['deleteTransfer']),
        ...mapActions('data/sharedAudience', ['stopSharedAudience']),
        ...mapActions('dashboardAccount', ['retrieveExpiringAudiences', 'retrieveExpiringTransfers']),
        // Call to mutations
        ...mapMutations('exports', ['setExportForm']),
        ...mapMutations('ui/monitoring', ['setDisplayModal', 'setDeletedAudienceId']),
        ...mapMutations('data/sharedAudience', ['setSharedAccountStatus', 'setIsProcessing']),
        ...mapMutations('ui/transfers', ['setShowFormTransfer', 'setShowFormCreateTransfer']),
        ...mapMutations('ui/volumeAlert', ['setShowVolumeAlertForm', 'setIsEditVolumeAlert']),


        bootstrapAudience() {
            this.$refs.details.displayEditSingleInputBootstrap()
        },

        changeViewName(name) {
            this.view_name = name
        },
        toggleMainActionMenu() {
            this.displayMainMenu = !this.displayMainMenu
        },
        exportDataForm(type) {
            this.setExportForm({
                display: true,
                type
            })
        },
        closeModal() {
            // We disallow the closing of modal by clicking anywhere else, to prevent weird behaviour with multi click on audience sharing switch input
            if (!isEmpty(this.deletedSharedAccount.id) || this.loadingDeleteAudience) return
            this.setIsProcessing({ isProcessing: false })
            this.setDisplayModal({ item: 'audience', display: false })
        },
        cancelStopSharing(partnerAccountId) {
            this.setSharedAccountStatus({ audienceId: this.selectedAudienceId, sharedAccountId: partnerAccountId })
            this.setIsProcessing({ isProcessing: false })
            this.setDisplayModal({ item: 'sharing', display: false })
        },
        stopSharing(partnerAccountId) {
            this.setDisplayModal({ item: 'sharing', display: false })
            this.stopSharedAudience({ partnerAccountId, audienceId: this.selectedAudienceId }).then(() => {
                this.setIsProcessing({ isProcessing: false })
            })
        },
        addVolumeAlert() {
            this.setIsEditVolumeAlert({ isEdit: false })
            this.setShowVolumeAlertForm({ show: true })
        },
        displayEditFormVolumeAlert() {
            this.setIsEditVolumeAlert({ isEdit: true })
            this.setShowVolumeAlertForm({ show: true })
        },
        deleteAudienceHandler() {
            this.setDisplayModal({ item: 'audience', display: true })
            this.setDeletedAudienceId({ audienceId: this.selectedAudienceId })
        },
        confirmDeleteAudience(audience_id){
            this.loadingDeleteAudience = true
            this.deleteAudience({audienceId: audience_id}).then(() => {
                this.loadingDeleteAudience = false
                this.retrieveExpiringAudiences()
                this.retrieveExpiringTransfers()
            })
        }
    }
}
</script>
